// ── CUA RANK SYSTEM ────────────────────────────────────────────────────────
// Global standard for ranking Computer-Use Agents by Smartness Index
export const CUA_RANKS = [
  { rank: 'Unranked',    min: 0,  max: 19,  icon: '◌', cls: 'unranked',    color: '#6b7280', bg: 'rgba(107,114,128,.12)', glow: 'none',                        desc: 'No evaluation completed' },
  { rank: 'Bronze',      min: 20, max: 34,  icon: '🥉', cls: 'bronze',      color: '#cd7f32', bg: 'rgba(205,127,50,.14)',  glow: '0 0 12px rgba(205,127,50,.3)', desc: 'Basic UI navigation demonstrated' },
  { rank: 'Silver',      min: 35, max: 49,  icon: '🥈', cls: 'silver',      color: '#9ca3af', bg: 'rgba(156,163,175,.14)', glow: '0 0 12px rgba(156,163,175,.3)', desc: 'Foundational task completion' },
  { rank: 'Gold',        min: 50, max: 64,  icon: '🥇', cls: 'gold',        color: '#f59e0b', bg: 'rgba(245,158,11,.14)',  glow: '0 0 16px rgba(245,158,11,.35)', desc: 'Reliable form & data interaction' },
  { rank: 'Platinum',    min: 65, max: 74,  icon: '💠', cls: 'platinum',    color: '#67e8f9', bg: 'rgba(103,232,249,.12)', glow: '0 0 16px rgba(103,232,249,.3)', desc: 'Handles complex navigation & logic' },
  { rank: 'Diamond',     min: 75, max: 84,  icon: '💎', cls: 'diamond',     color: '#818cf8', bg: 'rgba(129,140,248,.14)', glow: '0 0 20px rgba(129,140,248,.4)', desc: 'Expert-level reasoning & recovery' },
  { rank: 'Master',      min: 85, max: 94,  icon: '⚜️', cls: 'master',      color: '#a78bfa', bg: 'rgba(167,139,250,.16)', glow: '0 0 24px rgba(167,139,250,.5)', desc: 'Near-human UI mastery' },
  { rank: 'Grandmaster', min: 95, max: 100, icon: '👑', cls: 'grandmaster', color: '#fbbf24', bg: 'rgba(251,191,36,.18)',  glow: '0 0 32px rgba(251,191,36,.6)', desc: 'Superhuman CUA performance' },
];

export function computeCUARank(smartnessScore) {
  const s = smartnessScore ?? 0;
  const tier = CUA_RANKS.slice().reverse().find(r => s >= r.min) || CUA_RANKS[0];
  const next = CUA_RANKS[CUA_RANKS.indexOf(tier) + 1] || null;
  const progressPct = next
    ? Math.round(((s - tier.min) / (next.min - tier.min)) * 100)
    : 100;
  return { ...tier, next, progressPct, smartness: s };
}

// ── CHALLENGE TIERS ────────────────────────────────────────────────────────
// Maps challenge difficulty → challenge tier label (for display grouping)
export const CHALLENGE_TIERS = {
  Easy:   { tier: 'Foundational', color: '#22c55e', bg: 'rgba(34,197,94,.10)',   order: 1 },
  Medium: { tier: 'Intermediate', color: '#f59e0b', bg: 'rgba(245,158,11,.10)',  order: 2 },
  Hard:   { tier: 'Advanced',     color: '#f97316', bg: 'rgba(249,115,22,.10)',  order: 3 },
  Expert: { tier: 'Elite',        color: '#a78bfa', bg: 'rgba(167,139,250,.12)', order: 4 },
};

export function evaluateChallenge(challengeId, result) {
  const { score, details, passed } = result;
  const feedback = [];

  if (passed) {
    feedback.push({ type: 'pass', msg: '✅ All criteria met. Full marks awarded.' });
  } else {
    Object.entries(details || {}).forEach(([key, val]) => {
      if (!val.pass) {
        feedback.push({
          type: 'fail',
          msg: `❌ "${formatKey(key)}": Expected "${val.expected}", got "${val.got}"`,
        });
      } else {
        feedback.push({
          type: 'partial',
          msg: `✓ "${formatKey(key)}": Correct`,
        });
      }
    });
  }

  return {
    challengeId,
    score,
    passed,
    feedback,
    grade: scoreToGrade(score),
  };
}

function formatKey(key) {
  return key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()
    .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function scoreToGrade(score) {
  if (score === 100) return { label: 'PASS', cls: 'pass', color: '#2e7d32', bg: '#e8f5e9' };
  if (score >= 75) return { label: 'PARTIAL', cls: 'partial', color: '#e65100', bg: '#fff3e0' };
  if (score >= 40) return { label: 'WEAK', cls: 'weak', color: '#b71c1c', bg: '#fff8e1' };
  return { label: 'FAIL', cls: 'fail', color: '#c62828', bg: '#ffebee' };
}

export function computeOverallScore(results, challenges) {
  if (!results.length) return { raw: 0, weighted: 0, grade: scoreToGrade(0) };
  const totalPoints = challenges.reduce((s, c) => s + c.points, 0);
  const earned = results.reduce((s, r) => {
    const ch = challenges.find(c => c.id === r.challengeId);
    return s + (ch ? Math.round((r.score / 100) * ch.points) : 0);
  }, 0);
  const pct = Math.round((earned / totalPoints) * 100);
  return { raw: earned, total: totalPoints, pct, grade: scoreToGrade(pct) };
}
